// @flow

import styled from '@emotion/styled'
import { type PageProps, graphql } from 'gatsby'
import { GatsbyImage, StaticImage, getImage } from 'gatsby-plugin-image'
import * as React from 'react'
import type { Job } from 'types'

import { colors, fonts, media } from '@toggl/style'
import {
  Button,
  Card,
  CarouselNormal,
  ClientRender,
  IconInstagram,
  IconLinkedIn,
  Responsive,
  Search,
  SectionBlock,
  Table,
  text,
} from '@toggl/ui'
import { IconCheck } from '@toggl/ui/src/icons/IconCheck'
import { MDX, useFilters } from 'components'
import {
  FilterInfo,
  FilterList,
  FilterListItem,
  FilterPillList,
  FilterSection,
  FilterSectionWrapper,
  NoResults,
  ResultSection,
} from 'components/filter'
import { LayoutGlobal } from 'components/layouts'

import typeof JobsPageYaml from './jobs.yml'

const JobsPage = ({
  data,
}: PageProps<{
  jobsYaml: JobsPageYaml,
  allJobYaml: { nodes: $ReadOnlyArray<Job> },
}>) => {
  const {
    jobsYaml: {
      benefits,
      hiringSteps,
      workLife,
      testimonials,
      meetups,
      searchPlaceholder,
      searchMinJobsCount,
    },
    allJobYaml: { nodes: listedJobs },
  } = data
  const jobs = listedJobs
    .slice()
    .sort(
      (a, b) =>
        a.category.localeCompare(b.category) || a.title.localeCompare(b.title)
    )

  return (
    <LayoutGlobal
      title={'Work with Toggl’s Global Remote Team | Toggl'}
      description={
        'Our amazing team works from 40+ countries around the globe. We hire globally, you work locally. Check out our open positions!'
      }
      ogImage={require('assets/work-at-toggl.jpg').default}
    >
      <JobsHero solidColor={colors.darkPurple100}>
        <StaticImage
          formats={['auto', 'webp', 'avif']}
          src='assets/hero-humans.png'
          alt='Photos of teams of Toggl Track'
          width={350}
          sizes='(min-width: 800px) 300px, 200px'
          placeholder='none'
          objectFit='contain'
        />
        <StaticImage
          formats={['auto', 'webp', 'avif']}
          src='assets/hero-humans-2.png'
          alt='A photo of a Toggl Track team'
          width={500}
          sizes='(min-width: 800px) 300px, 200px'
          placeholder='none'
          objectFit='contain'
        />
        <text.H1>Become a Toggler.</text.H1>
        <text.P1 color={colors.orange10}>
          Join our growing team to build the world's most-loved productivity
          tools
        </text.P1>

        {jobs.length > 0 && (
          <Button.Primary as='a' href='#jobs'>
            See {jobs.length} job openings
          </Button.Primary>
        )}
      </JobsHero>

      <SectionBlock background={colors.peach30}>
        <SectionTitle>
          <text.H2 color={colors.darkPurple90}>Work life at Toggl</text.H2>
        </SectionTitle>
        <WorkLife>
          {workLife.map((point, index) => {
            return (
              <WorkLifeFeature
                key={index}
                image={
                  <GatsbyImage
                    image={getImage(point.image.src)}
                    alt={point.image.alt}
                    objectFit='contain'
                  />
                }
                title={point.title}
                paragraph={point.description}
                link={point.cta?.href}
                cta={point.cta?.text}
                icon
                largeButton
              />
            )
          })}
        </WorkLife>
      </SectionBlock>

      <MeetupImages background={colors.peach30}>
        <text.UppercaseOneLiner color={colors.darkPurple90}>
          Check out our recent meetups
        </text.UppercaseOneLiner>
        <CarouselNormal show={5}>
          {meetups.map((meetup, index) => (
            <MeetupImage key={index}>
              <GatsbyImage
                image={getImage(meetup.image.src)}
                alt={meetup.image.alt}
                objectFit='contain'
              />
            </MeetupImage>
          ))}
        </CarouselNormal>
      </MeetupImages>

      <SectionBlock background={colors.peach50}>
        <text.H2 color={colors.darkPurple90}>Why Togglers love it here</text.H2>

        <CarouselNormal show={3}>
          {testimonials.map((testimonial, index) => (
            <Testimonial key={index}>
              <GatsbyImage
                image={getImage(testimonial.image.src)}
                alt={testimonial.image.alt}
                objectFit='contain'
              />
              <text.P3 color={colors.darkPurple80}>
                {testimonial.paragraph}
              </text.P3>
              <HumanIdentity>
                <div>
                  <text.P3 color={colors.darkPurple90}>
                    <text.Strong>{testimonial.name}</text.Strong>
                  </text.P3>
                  <text.P3 color={colors.darkPurple60}>
                    <text.Strong>{testimonial.title}</text.Strong>
                  </text.P3>
                </div>
              </HumanIdentity>
            </Testimonial>
          ))}
        </CarouselNormal>
      </SectionBlock>

      <WeAreDifferentSection background={colors.peach50}>
        <img
          src={require('./assets/toggl-heart.svg').default}
          alt={'A heart icon with 3 colors: purple, pink and orange'}
          loading='lazy'
        />
        <WeAreDifferent color={colors.darkPurple90}>
          We're united by our shared commitment to autonomy and high-quality
          work. You'll be joining a team of smart, passionate colleagues who
          don't shy away from a challenge or sharing their thoughts. Sound like
          your jam? <a href='#jobs'>Come say hi!</a>
        </WeAreDifferent>
        <PhotoGrid>
          <div>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src='assets/photo-zoom-call.jpg'
              alt='Screenshot of a Zoom call with Togglers'
              width={720}
              placeholder='none'
              objectFit='cover'
            />
          </div>
          <div>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src='assets/meetup-meeting.jpg'
              alt='Photo of the people in a conference room, having a meeting'
              width={720}
              placeholder='none'
              objectFit='cover'
            />
          </div>
          <Responsive.DesktopOnly>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src='assets/meetup-edinburgh.jpg'
              alt='Photo of the a Toggl team in a meetup in Edinburgh'
              width={310}
              placeholder='none'
              objectFit='cover'
            />
          </Responsive.DesktopOnly>
          <Responsive.DesktopOnly>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src='assets/laptop-dog.jpg'
              alt='Photo of a Toggl employee with his dog on Zoom call'
              width={310}
              placeholder='none'
              objectFit='cover'
            />
          </Responsive.DesktopOnly>
          <Responsive.DesktopOnly>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src='assets/meetup-phuket-2.jpg'
              alt='Photo of the apps team in a meetup in Phuket'
              width={320}
              placeholder='none'
              objectFit='cover'
            />
          </Responsive.DesktopOnly>
          <Responsive.DesktopOnly>
            <StaticImage
              formats={['auto', 'webp', 'avif']}
              src='assets/meetup-seville.jpg'
              alt='Photo of the Revenue department in a meetup in Seville'
              width={360}
              placeholder='none'
              objectFit='cover'
            />
          </Responsive.DesktopOnly>
          <StatHorizontal>
            <Stat>
              <text.H2 color={colors.darkPurple100}>43</text.H2>
              <text.P4 color={colors.darkPurple80}>
                countries in 16 timezones
              </text.P4>
            </Stat>
            <LanguageStat>
              <text.H2 color={colors.darkPurple100}>37</text.H2>
              <text.P4 color={colors.darkPurple80}>languages spoken</text.P4>
            </LanguageStat>
          </StatHorizontal>

          <StatVertical>
            <Stat>
              <text.H2 color={colors.darkPurple100}>4.9</text.H2>
              <StaticImage
                formats={['auto', 'webp', 'avif']}
                src='assets/icon-stars.png'
                alt='Review stars: 4.9 out of 5'
                width={95}
                placeholder='none'
                objectFit='contain'
              />
              <text.P4 color={colors.darkPurple80}>
                On glassdoor with 44 reviews
              </text.P4>
            </Stat>
            <PetStat>
              <text.H2 color={colors.darkPurple100}>113</text.H2>
              <text.P4 color={colors.darkPurple80}>
                cats, dogs, chickens, and more!
              </text.P4>
            </PetStat>
          </StatVertical>
          <InstaPlug>
            <text.P2 color={colors.darkPurple90}>
              Learn more about Toggl and stay updated on our activities:
            </text.P2>
            <div>
              <a
                href='https://www.instagram.com/toggltrack'
                target='_blank'
                rel='noreferrer'
                aria-label="Visit Toggl's Instagram profile"
              >
                <IconInstagram />
              </a>

              <a
                href='https://www.linkedin.com/company/toggl/'
                rel='noreferrer'
                aria-label="Visit Toggl's LinkedIn profile"
                target='_blank'
              >
                <IconLinkedIn />
              </a>
            </div>
          </InstaPlug>
        </PhotoGrid>
      </WeAreDifferentSection>

      <SectionBlock background={colors.darkPurple90}>
        <SectionTitle>
          <text.UppercaseOneLiner color={colors.darkPurple20}>
            Perks & Benefits
          </text.UppercaseOneLiner>

          <text.H2 color={colors.yellow100}>We'll sweeten the deal</text.H2>
        </SectionTitle>
        <BenefitItems>
          {benefits.map((benefit, index) => (
            <BenefitItem key={index}>
              <BenefitEmoji>{benefit.emoji}</BenefitEmoji>
              <text.P2 color={colors.peach10}>{benefit.title}</text.P2>
            </BenefitItem>
          ))}
        </BenefitItems>
      </SectionBlock>

      <HiringSection background={colors.yellow20}>
        <SectionTitle>
          <text.H2 color={colors.darkPurple90}>Our hiring process</text.H2>
          <text.P2 color={colors.darkPurple70}>
            There might be slight differences based on the position and
            seniority. For the most accurate information, please visit the
            individual job posting.
          </text.P2>
        </SectionTitle>
        <HiringBlock>
          <div>
            {hiringSteps.map((step, index) => {
              return (
                <HiringStep key={index}>
                  <GatsbyImage
                    image={getImage(step.image.src)}
                    alt={step.image.alt}
                    objectFit='contain'
                  />
                  <div>
                    <text.H5 color={colors.darkPurple90}>{step.title}</text.H5>
                    <MDX p={text.P3}>{step.description}</MDX>
                  </div>
                </HiringStep>
              )
            })}
          </div>
          <HiringTestimonial>
            <text.P2 color={colors.darkPurple90}>
              “Everything about the hiring process was very fluid and
              transparent. From the initial test to the cultural interview, and
              to the role-specific tasks, I felt well-guided and informed as to
              what the next steps were.”
            </text.P2>
            <HumanIdentity>
              <img
                src={require('./assets/toggler-georgina.jpg').default}
                alt='Profile photo of Georgina from Toggl Track'
                loading='lazy'
              />
              <div>
                <text.P3 color={colors.darkPurple90}>
                  <text.Strong>Georgina Ochoa</text.Strong>
                </text.P3>
                <text.P3 color={colors.darkPurple60}>
                  <text.Strong>Account Executive</text.Strong>
                </text.P3>
              </div>
            </HumanIdentity>
          </HiringTestimonial>
          <HiringTestimonial>
            <text.P2 color={colors.darkPurple90}>
              “Toggl’s hiring process, particularly the Test Week, allowed me to
              ensure the company would be a good fit for me. During that time, I
              was able to meet the team and get a better grip on what kinds of
              tasks I would be expected to do in the role.”
            </text.P2>
            <HumanIdentity>
              <img
                src={require('./assets/toggler-rodrigo.jpg').default}
                alt='Profile photo of Rodrigo from Toggl Track'
                loading='lazy'
              />
              <div>
                <text.P3 color={colors.darkPurple90}>
                  <text.Strong>Rodrigo Souza</text.Strong>
                </text.P3>
                <text.P3 color={colors.darkPurple60}>
                  <text.Strong>Analytics Engineer</text.Strong>
                </text.P3>
              </div>
            </HumanIdentity>
          </HiringTestimonial>
        </HiringBlock>
      </HiringSection>

      <SectionBlock background={colors.peach10} id='jobs'>
        <SectionTitle>
          <text.H2 color={colors.darkPurple90}>Our current openings</text.H2>
        </SectionTitle>

        <ClientRender hideServerRender>
          <JobListAndFilter
            jobs={jobs}
            search={jobs.length > searchMinJobsCount}
            searchPlaceholder={searchPlaceholder}
          />
        </ClientRender>

        {jobs.length === 0 && (
          <NoPositions color={colors.darkPurple70}>
            Sorry, there are no open positions at this time.
          </NoPositions>
        )}
      </SectionBlock>
    </LayoutGlobal>
  )
}

const defaultFilters = {
  category: null,
  location: null,
  term: null,
}

type JobListAndFilterProps = {
  +jobs: $ReadOnlyArray<Job>,
  +search: boolean,
  +searchPlaceholder: string,
}

const JobListAndFilter = ({
  jobs,
  search,
  searchPlaceholder,
}: JobListAndFilterProps) => {
  const categories = [...new Set(jobs.map(({ category }) => category))]
  const locations = [...new Set(jobs.map(({ location }) => location))]
  const {
    filters,
    applyFilter,
    onSearch,
    areEmptyFilters,
    filtered: visibleJobs,
    resetFilters,
  } = useFilters({
    defaultFilters,
    filter: (items, filters) => {
      const filterTerm = filters.term?.toLowerCase() || ''
      return items.filter(
        ({ category, location, title }) =>
          title.toLowerCase().includes(filterTerm) &&
          (!filters.category || category.includes(filters.category)) &&
          (!filters.location || location.includes(filters.location))
      )
    },
    items: jobs,
    keyToParams: { location: 'loc' },
  })

  return (
    <FilterWithResults background={colors.peach10}>
      <FilterSectionWrapper>
        {search && (
          <Search
            onSearch={onSearch}
            placeholder={searchPlaceholder}
            initialValue={filters.term}
          />
        )}

        <FilterSection>
          <text.P4>DEPARTMENT:</text.P4>
          <FilterList>
            {categories.map((category) => (
              <FilterListItem
                key={category}
                active={filters.category === category}
                onClick={() =>
                  applyFilter({
                    category: filters.category !== category ? category : null,
                  })
                }
              >
                <IconCheck />
                {category}
              </FilterListItem>
            ))}
          </FilterList>
        </FilterSection>

        <FilterSection>
          <text.P4>LOCATION:</text.P4>
          <FilterList>
            {locations.map((location) => (
              <FilterListItem
                key={location}
                active={filters.location === location}
                onClick={() =>
                  applyFilter({
                    location: filters.location !== location ? location : null,
                  })
                }
              >
                <IconCheck />
                {location}
              </FilterListItem>
            ))}
          </FilterList>
        </FilterSection>
      </FilterSectionWrapper>
      <ResultSection>
        <FilterInfo>
          <strong>
            {!areEmptyFilters &&
              `${visibleJobs.length} ${
                visibleJobs.length !== 1 ? 'results' : 'result'
              }`}
          </strong>
          {!areEmptyFilters && ` filtered by `}
          <FilterPillList
            filters={filters}
            filterKeys={['term', 'category', 'location']}
            applyFilter={applyFilter}
          />
        </FilterInfo>
        {visibleJobs.length > 0 ? (
          <TableWrapper>
            <JobListings jobs={visibleJobs} />
          </TableWrapper>
        ) : (
          <NoResults>
            Oops, there are no results. You should try broadening your search
            <Button.Primary onClick={resetFilters}>
              Remove filters
            </Button.Primary>
          </NoResults>
        )}
      </ResultSection>
    </FilterWithResults>
  )
}

export const query = graphql`
  {
    jobsYaml {
      benefits {
        emoji
        title
      }
      hiringSteps {
        description {
          body
        }
        title
        image {
          alt
          src {
            childImageSharp {
              gatsbyImageData(
                width: 85
                formats: [AUTO, WEBP, AVIF]
                placeholder: NONE
              )
            }
          }
        }
      }
      workLife {
        title
        description
        image {
          alt
          src {
            childImageSharp {
              gatsbyImageData(
                height: 100
                formats: [AUTO, WEBP, AVIF]
                placeholder: NONE
              )
            }
          }
        }
        cta {
          text
          href
        }
      }
      testimonials {
        image {
          alt
          src {
            childImageSharp {
              gatsbyImageData(
                width: 500
                height: 340
                formats: [AUTO, WEBP, AVIF]
                placeholder: NONE
                sizes: "(min-width: 800px) 350px, 500px"
              )
            }
          }
        }
        paragraph
        title
        name
      }
      meetups {
        image {
          alt
          src {
            childImageSharp {
              gatsbyImageData(
                width: 500
                height: 823
                formats: [AUTO, WEBP, AVIF]
                placeholder: NONE
                sizes: "(min-width: 800px) 266px, 500px"
              )
            }
          }
        }
      }
      searchPlaceholder
      searchMinJobsCount
    }
    allJobYaml(filter: { listed: { eq: true } }) {
      nodes {
        aboutYou {
          body
        }
        category
        intro {
          body
        }
        listed
        location
        published
        role {
          body
        }
        slug
        team
        testUrl
        title
      }
    }
  }
`

function JobListings({ jobs }) {
  if (jobs.length === 0) return null
  return <Table.ThreeCol jobs={jobs} />
}

const NoPositions = styled(text.P1)`
  margin: 45px auto;
  text-align: center;
`

const TableWrapper = styled.div`
  margin-top: 20px;
`

const JobsHero = styled.div`
  padding: 120px 30px 45px;
  position: relative;
  text-align: center;
  h1 {
    width: max-content;
    line-height: 1.25;
    margin: auto;
    background: -webkit-linear-gradient(
      right,
      ${colors.orange},
      ${colors.pink100},
      ${colors.violet}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
  p {
    font-size: 130%;
    position: relative;
    max-width: 35ch;
    margin: 25px auto;
    ${media.mq[2]} {
      font-size: 150%;
    }
  }
  a {
    display: inline-flex;
    margin: 15px auto;
  }
  ${media.mq[1]} {
    padding-top: 150px;
  }
  ${media.mq[2]} {
    padding-bottom: 60px;
  }
  .gatsby-image-wrapper {
    position: absolute;
    &:nth-of-type(1) {
      left: 5%;
      width: 18%;
      bottom: 10%;
      max-width: 300px;
    }
    &:nth-of-type(2) {
      right: 0;
      width: 22%;
      bottom: -8%;
      max-width: 400px;
    }
  }
`

const WorkLife = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 35px;
  column-gap: 60px;
  > div {
    flex: 1 1 25%;
    min-width: 250px;
  }
  ${media.mq[1]} {
    row-gap: 15px;
    margin-top: 60px;
  }
`

const WorkLifeFeature = styled(Card.FeatureIcon)`
  a {
    background: ${colors.peach100};
    padding: 9px 12px 9px 15px;
    &:hover {
      background: ${colors.yellow100};
      color: ${colors.darkPurple100};
    }
  }
`

const MeetupImages = styled(SectionBlock)`
  padding-top: 0px !important;
  p {
    margin-bottom: 8px;
  }
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }
`

const MeetupImage = styled.div`
  padding: 15px 30px 15px 0;
  ${media.mq[1]} {
    padding: 15px;
  }
  border-radius: 10px;
  overflow: hidden;
`

const SectionTitle = styled.div`
  text-align: center;
  h2 {
    margin-top: 20px;
    ~ p {
      max-width: 55ch;
      margin: 25px auto;
    }
  }
`

const WeAreDifferentSection = styled(SectionBlock)`
  padding-top: 0 !important;
  text-align: center;
  > div > img {
    display: block;
    margin: 0 auto 30px;
    max-width: 60px;
  }
`

const WeAreDifferent = styled(text.H3)`
  max-width: 50ch;
  line-height: 1.6;
  margin: auto;
  ${media.mq[1]} {
    width: 80%;
  }
  a {
    background: -webkit-linear-gradient(
      right,
      ${colors.orange},
      ${colors.pink100},
      ${colors.violet}
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    text=decoration: underline;
    &:hover {
      color: ${colors.darkPurple70};
      font-family: ${fonts.gtHaptikMediumRotalic};
    }
  }
`

const PhotoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(4, minmax(6rem, auto));
  grid-gap: 15px;
  margin-top: 30px;
  > div:nth-of-type(-n + 6) {
    border-radius: 10px;
    overflow: hidden;
    &:nth-of-type(1),
    &:nth-of-type(2) {
      grid-column: span 2;
    }
  }
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }
  ${media.mq[1]} {
    margin: 60px auto 30px;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(3, minmax(6rem, auto));
  }
  ${media.mq[2]} {
    grid-template-columns: 0.5fr 22.5rem 2fr 11rem 0.4fr 0.2fr;
    grid-template-rows: 6rem 10rem repeat(5, minmax(5rem, auto));
    grid-gap: 25px;
    > div:nth-of-type(1) {
      grid-column: 1 / 3;
      grid-row: 2 / 4;
    }
    > div:nth-of-type(2) {
      grid-row: 1 / 3;
      grid-column: 3 / 6;
    }
    > div:nth-of-type(3) {
      grid-column: 3 / 4;
      grid-row: span 2;
    }
    > div:nth-of-type(4) {
      grid-row: 5 / 7;
      grid-column: 3 / 4;
    }
    > div:nth-of-type(5) {
      grid-column: 4 / -1;
      grid-row: span 2;
    }
    > div:nth-of-type(6) {
      grid-column: 2 / 3;
      grid-row: 5 / 8;
    }
  }
`

const InstaPlug = styled.div`
  grid-column: 1 / -1;
  padding: 15px;
  ${media.mq[2]} {
    grid-column: 3/4;
    grid-row: 7/8;
    border-radius: 10px;
    background: ${colors.peach100};
  }
  p {
    font-family: ${fonts.gtHaptikMedium};
    line-height: 1.4;
    margin-bottom: 5px;
  }
  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 1000px;
    width: 35px;
    height: 35px;
    margin: 3px;
    background: ${colors.darkPurple100};
    &:hover {
      background: ${colors.darkPurple70};
    }
  }
  svg {
    width: 15px;
    height: 15px;
    object-fit: contain;
  }
`

const StatHorizontal = styled.div`
  display: flex;
  gap: 15px;
  grid-column: span 2;
  ${media.mq[2]} {
    grid-column: 2 / 3;
    grid-row: 4 / 5;
    gap: 25px;
  }
`

const StatVertical = styled(StatHorizontal)`
  ${media.mq[2]} {
    grid-column: 4/5;
    grid-row: 3/6;
    flex-direction: column;
  }
`

const Stat = styled.div`
  position: relative;
  min-height: 10rem;
  background: ${colors.peach100};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding: 1.35rem;
  text-align: center;
  .gatsby-image-wrapper {
    max-width: 95px;
    max-height: 25px;
  }
  p {
    text-transform: uppercase;
    font-weight: bold;
    margin-top: 3px;
  }
`

const LanguageStat = styled(Stat)`
  &:before,
  &:after {
    position: absolute;
    content: '';
    background-size: contain;
    background-repeat: no-repeat;
  }
  &:before {
    background-image: url(${require('./assets/language-portuguese.png')
      .default});
    top: 8px;
    right: -12px;
    width: 70px;
    height: 40px;
  }
  &:after {
    background-image: url(${require('./assets/languages-german-spanish.png')
      .default});
    bottom: -15px;
    left: -15px;
    width: 76px;
    height: 55px;
  }
`

const PetStat = styled(Stat)`
  &:before {
    position: absolute;
    content: '';
    background-image: url(${require('./assets/icon-dog.png').default});
    background-size: contain;
    background-repeat: no-repeat;
    width: 60px;
    height: 75px;
    top: -15px;
    right: -10px;
  }
`

const BenefitItems = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  column-gap: 40px;
  row-gap: 50px;
  margin-top: 60px;
  ${media.mq[2]} {
    margin-bottom: 30px;
  }
`

const BenefitItem = styled.div`
  font-family: ${fonts.gtHaptikMedium};
  p {
    line-height: 1.35;
  }
  text-align: center;
  min-width: 180px;
  flex: 1 1 15%;
`

const BenefitEmoji = styled.span`
  display: flex;
  margin: auto;
  align-items: center;
  justify-content: center;
  width: 60px;
  height: 60px;
  background: ${colors.yellow40};
  border-radius: 100%;
  margin-bottom: 15px;
  font-size: 200%;
`

const HiringBlock = styled.div`
  margin-top: 45px;
  ${media.mq[1]} {
    display: grid;
    grid-template-columns: 1.6fr 1fr;
    column-gap: 70px;
    align-items: flex-start;
    > div:nth-of-type(1) {
      grid-row: span 2;
    }
  }
`

const HiringStep = styled.div`
  display: flex;
  align-items: middle;
  gap: 20px;
  margin: 35px 0;
  position: relative;
  &:not(:last-of-type):before {
    content: '';
    height: 100%;
    position: absolute;
    width: 6px;
    background: ${colors.yellow100};
    top: 70%;
    left: 37px;
  }
  a {
    color: ${colors.darkPurple70};
    text-decoration: underline;
    &:hover {
      color: ${colors.orange};
    }
  }
  p {
    margin-top: 5px;
    color: ${colors.darkPurple70};
  }
  .gatsby-image-wrapper {
    flex: 0 0 80px;
  }
`

const HumanIdentity = styled.div`
  display: flex;
  margin-top: 15px;
  gap: 12px;
  align-items: center;
  img {
    width: 50px;
    height: 50px;
    border-radius: 100px;
  }
  p {
    line-height: 1.4;
  }
`

const Testimonial = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 20px 35px 20px 0;
  ${media.mq[1]} {
    padding: 20px;
  }
  ${HumanIdentity} {
    margin-top: auto;
  }
  .gatsby-image-wrapper {
    margin-bottom: 10px;
    width: 100%;
    border-radius: 10px;
  }
`

const HiringSection = styled(SectionBlock)`
  position: relative;
  overflow: hidden;
  &:before {
    content: url(${require('./assets/shapes-hiring.svg').default});
    position: absolute;
    top: -80px;
    left: -80px;
    z-index: 0;
  }
  > div {
    position: relative;
  }
`

const HiringTestimonial = styled.div`
  background: ${colors.orange20};
  padding: 35px;
  border-radius: 10px;
  margin: 15px 0;
  ${media.mq[1]} {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
`

const FilterWithResults = styled.div`
  ${media.mq[1]} {
    display: flex;
    gap: 30px;
    margin-top: 50px;
  }
`

export default JobsPage
